import React from 'react';
import { Link2, MapPin } from 'lucide-react';
import verifiedIcon from '../assets/verified.svg';

export interface HeaderProps {
  companyName: string;
  location: string;
  websiteText: string;
  servicesText: string;
  industriesText: string;
  introCallCertified?: boolean;
  headerAction?: React.ReactNode;
  contentAction?: React.ReactNode;
}

function normalizeWebsiteUrl(raw: string) {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `https://${trimmed}`;
}

function getDomainFromWebsite(raw: string) {
  const normalized = normalizeWebsiteUrl(raw);
  if (!normalized) return '';
  try {
    const host = new URL(normalized).hostname;
    return host.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function getInitials(name: string) {
  const parts = name
    .split(/\s+/)
    .map((p) => p.trim())
    .filter((p) => !!p);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : parts[0]?.[1] ?? '';
  return (first + last).toUpperCase();
}

export default function Header({
  companyName,
  location,
  websiteText,
  servicesText,
  industriesText,
  introCallCertified = false,
  headerAction,
  contentAction,
}: HeaderProps) {
  const [logoFailed, setLogoFailed] = React.useState(false);
  const domain = getDomainFromWebsite(websiteText);
  const initials = getInitials(companyName);
  const logoSrc = domain ? `https://cdn.brandfetch.io/${domain}/w/120/h/80` : '';

  return (
    <div className="w-full">
      <div className="relative h-[159px] w-full overflow-hidden rounded-[12px] border border-[#c1c1c1]">
        <div className="absolute inset-0 bg-[#132543]" />
        {headerAction && (
          <div className="absolute right-[20px] top-1/2 -translate-y-1/2" data-pdf-exclude>
            {headerAction}
          </div>
        )}
      </div>

      <div className="-mt-[60px] flex flex-col items-start px-[13px]">
        <div className="relative h-[173.332px] w-[179.045px] shrink-0">
          <div className="absolute inset-0 rounded-full bg-white" style={{ border: '0.589566px solid #D9D9D9' }} />

          <div className="absolute left-[19px] top-[44px] flex h-[84.967px] w-[141.612px] items-center justify-center">
            {domain && !logoFailed ? (
              <img
                id="provider-logo-img"
                src={logoSrc}
                alt={companyName || 'Logo'}
                loading="lazy"
                crossOrigin="anonymous"
                className="block max-h-full max-w-full rounded object-contain object-center bg-white"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <div className="absolute inset-0 rounded flex items-center justify-center">
                <span
                  className="font-['Hanken_Grotesk',sans-serif] text-[70px] font-bold text-[#132543]"
                  style={{ fontVariationSettings: "'wdth' 100" }}
                >
                  {initials}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-[10px] flex w-full min-w-0 items-start justify-between pb-[10px]">
          <div className="flex flex-col gap-[10px]">
          <div className="flex items-start gap-[10px]">
            <p
              className="font-['Hanken_Grotesk',sans-serif] text-[32px] font-bold leading-[40px] text-[#132543]"
              style={{ fontVariationSettings: "'wdth' 100", letterSpacing: '0.288px' }}
            >
              {companyName}
            </p>
            {introCallCertified && (
              <span className="relative group mt-[5px] inline-flex shrink-0 items-center">
                <img src={verifiedIcon} alt="BluWave certified" className="size-[30px]" />
                <span className="pointer-events-none absolute bottom-full left-1/2 mb-2 w-max -translate-x-1/2 rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                  This provider has been BluWave certified
                </span>
              </span>
            )}
          </div>

          {(!!location || !!websiteText) && (
            <div className="flex items-center gap-[10px]">
              <div className="inline-flex h-[33px] items-center gap-[3px] rounded-[25px] px-[12px] py-[10px] whitespace-nowrap">
                <MapPin size={13} color="#31394C" />
                {!!location && (
                  <p
                    className="font-['Open_Sans',sans-serif] text-[14px] font-semibold leading-[normal] text-[#31394C]"
                    style={{ fontVariationSettings: "'wdth' 100" }}
                  >
                    {location}
                  </p>
                )}
              </div>

              {!!location && !!websiteText && (
                <span className="text-[#C0C0C0]" aria-hidden="true">
                  •
                </span>
              )}

              {!!websiteText && (
                <a
                  className="inline-flex h-[33px] items-center gap-[3px] rounded-[25px] px-[12px] py-[10px] whitespace-nowrap"
                  href={normalizeWebsiteUrl(websiteText)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Link2 size={19} color="#142745" />
                  <span
                    className="font-['Open_Sans',sans-serif] text-[14px] font-semibold leading-[normal] text-[#31394C]"
                    style={{ fontVariationSettings: "'wdth' 100" }}
                  >
                    Website
                  </span>
                </a>
              )}
            </div>
          )}
          </div>
          {contentAction && (
            <div className="ml-4 shrink-0 self-center" data-pdf-exclude>
              {contentAction}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
